import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../../components/layout';
import { Download, Save, ArrowLeft } from 'lucide-react';

const CoverLetterPage = () => {
  const navigate = useNavigate();
  const [coverLetter, setCoverLetter] = useState({
    title: '자기소개서',
    sections: {
      growth: '',
      personality: '',
      motivation: ''
    },
    createdAt: new Date().toLocaleDateString(),
    updatedAt: new Date().toLocaleDateString()
  });

  const handleSectionChange = (section, content) => {
    setCoverLetter(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [section]: content
      },
      updatedAt: new Date().toLocaleDateString()
    }));
  };

  const handleSave = () => {
    console.log('Saving cover letter...', coverLetter);
    alert('자기소개서가 저장되었습니다!');
  };

  const handleDownload = () => {
    console.log('Downloading cover letter...');
    alert('PDF 다운로드 기능을 구현 중입니다!');
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="자기소개서 작성" />
      
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/consulting')}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              컨설팅으로 돌아가기
            </button>
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md"
            >
              <ArrowLeft className="w-4 h-4" />
              목록으로
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">자기소개서</h2>
              <p className="text-gray-600">나만의 스토리를 작성해보세요</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              <Save className="w-4 h-4" />
              저장
            </button>
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
            >
              <Download className="w-4 h-4" />
              PDF 다운로드
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* 헤더 */}
          <div className="p-6 border-b border-gray-200">
            <input
              type="text"
              value={coverLetter.title}
              onChange={(e) => setCoverLetter(prev => ({ ...prev, title: e.target.value }))}
              className="text-2xl font-bold text-gray-900 bg-transparent border-none outline-none w-full"
              placeholder="자기소개서 제목"
            />
            <div className="flex gap-4 text-sm text-gray-500 mt-2">
              <span>작성일: {coverLetter.createdAt}</span>
              <span>수정일: {coverLetter.updatedAt}</span>
            </div>
          </div>

          {/* 본문 */}
          <div className="p-6 space-y-8">
            {/* 성장 과정 */}
            <div>
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">1. 성장 과정</h3>
                <p className="text-sm text-gray-600">
                  직무 역량과 관련된 경험 중심으로 간략히 작성해주세요
                </p>
              </div>
              <textarea
                value={coverLetter.sections.growth}
                onChange={(e) => handleSectionChange('growth', e.target.value)}
                className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={8}
                placeholder="성장 과정에서의 주요 경험과 배움을 작성해주세요...

예시:
어린 시절부터 컴퓨터에 관심이 많았던 저는 대학에서 컴퓨터공학을 전공하며 프로그래밍의 매력에 빠지게 되었습니다. 특히 팀 프로젝트를 통해 협업의 중요성을 깨달았고, 문제 해결 과정에서 논리적 사고력을 기를 수 있었습니다..."
              />
            </div>

            {/* 성격의 장단점 */}
            <div>
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">2. 성격의 장단점</h3>
                <p className="text-sm text-gray-600">
                  직무 수행에 긍정적인 영향을 주는 장점과 개선 노력을 포함한 단점
                </p>
              </div>
              <textarea
                value={coverLetter.sections.personality}
                onChange={(e) => handleSectionChange('personality', e.target.value)}
                className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={8}
                placeholder="자신의 장점과 단점, 그리고 개선 노력을 작성해주세요...

예시:
제 가장 큰 장점은 꼼꼼함과 책임감입니다. 맡은 업무는 끝까지 완수하려고 노력하며, 세부사항까지 놓치지 않으려 합니다. 반면 완벽주의적 성향으로 인해 때로는 일정에 여유를 두지 못하는 단점이 있습니다. 이를 개선하기 위해 우선순위를 명확히 하고 시간 관리 도구를 활용하고 있습니다..."
              />
            </div>

            {/* 지원 동기 */}
            <div>
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">3. 지원 동기</h3>
                <p className="text-sm text-gray-600">
                  회사 및 직무에 대한 이해와 관심, 본인의 기여 가능성
                </p>
              </div>
              <textarea
                value={coverLetter.sections.motivation}
                onChange={(e) => handleSectionChange('motivation', e.target.value)}
                className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={8}
                placeholder="지원 동기와 입사 후 기여할 수 있는 부분을 작성해주세요...

예시:
귀사의 혁신적인 기술력과 사용자 중심의 서비스 철학에 깊은 감명을 받았습니다. 특히 최근 출시하신 AI 기반 솔루션은 업계에 새로운 패러다임을 제시했다고 생각합니다. 저의 5년간의 개발 경험과 새로운 기술에 대한 열정으로 귀사의 성장에 기여하고 싶습니다..."
              />
            </div>
          </div>
        </div>

        {/* 미리보기 */}
        <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">미리보기</h3>
          <div className="prose prose-sm max-w-none">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold">{coverLetter.title}</h1>
            </div>
            
            {coverLetter.sections.growth && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold mb-3">1. 성장 과정</h2>
                <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                  {coverLetter.sections.growth}
                </p>
              </div>
            )}

            {coverLetter.sections.personality && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold mb-3">2. 성격의 장단점</h2>
                <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                  {coverLetter.sections.personality}
                </p>
              </div>
            )}

            {coverLetter.sections.motivation && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold mb-3">3. 지원 동기</h2>
                <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                  {coverLetter.sections.motivation}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoverLetterPage;
